"use client";

import type { ReactElement } from "react";

import { Feedback } from "@/components/dashboard/Feedback";
import enDesktop from "@/locales/en/desktop.json";
import zhDesktop from "@/locales/zh/desktop.json";
import { getDesktopDiagnosticsReport } from "@/lib/desktop/diagnostics";
import {
  buildDesktopIssueSceneContents,
  transformDesktopIssueSubmitContent,
} from "@/lib/desktop/issue-report";
import { useI18nNamespace } from "@/hooks/useI18n";

const DESKTOP_ISSUE_FIELDS = ["content", "contact"];
type DesktopIssueCopyKey = keyof typeof zhDesktop.issueReport;

export function DesktopIssueReporter({
  autoOpenKey,
  children,
}: {
  autoOpenKey?: string | number | null;
  children: ReactElement;
}) {
  const { currentLanguage, t } = useI18nNamespace("desktop");
  const issueReportFallback = currentLanguage === "en"
    ? enDesktop.issueReport
    : zhDesktop.issueReport;
  const issueReportText = (key: DesktopIssueCopyKey) => t(
    `issueReport.${key}`,
    { defaultValue: issueReportFallback[key] },
  );

  return (
    <Feedback
      scene="desktopIssue"
      autoOpenKey={autoOpenKey}
      prepareSceneContents={async () => (
        buildDesktopIssueSceneContents(await getDesktopDiagnosticsReport())
      )}
      transformSubmitContent={transformDesktopIssueSubmitContent}
      presentation={{
        title: issueReportText("title"),
        description: issueReportText("description"),
        fieldKeys: DESKTOP_ISSUE_FIELDS,
        requireContent: true,
        contentLabel: issueReportText("contentLabel"),
        contentPlaceholder: issueReportText("contentPlaceholder"),
        cancelLabel: issueReportText("cancel"),
        submitLabel: issueReportText("submit"),
        loadingMessage: issueReportText("submitting"),
        successMessage: issueReportText("success"),
        failureMessage: issueReportText("failure"),
      }}
    >
      {children}
    </Feedback>
  );
}
